import React from "react";
import { Link } from "react-router-dom";
import {
    Server,
    Network,
    Layers,
    KeyRound,
    Search,
    FileDown,
    ArrowRight,
    Check,
    Shield,
} from "lucide-react";
import { Reveal, RevealItem } from "../components/Reveal";
import { SEO, SITE_DOMAIN } from "../components/SEO";
import { Breadcrumb } from "../components/Breadcrumb";
import { BREADCRUMBS } from "../lib/pageMetadata";

/**
 * /ipam — marketing landing for the Client Data Suite (IPAM).
 * Public, crawlable. NOT the gated app (that's /clients).
 */
export const Ipam = () => {
    const productSchema = {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "@id": `${SITE_DOMAIN}/ipam#product`,
        name: "Client Data Suite — Lightweight IPAM",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        url: `${SITE_DOMAIN}/ipam`,
        description:
            "Multi-tenant IPAM for SMB network engineers and small MSPs. Track clients, subnets, hardware (43 pre-cataloged models, 10 vendors), and important IPs with CPE auto-population for future CVE matching.",
        publisher: { "@id": `${SITE_DOMAIN}/#organization` },
        offers: {
            "@type": "Offer",
            name: "Pro",
            price: "19.90",
            priceCurrency: "USD",
            url: `${SITE_DOMAIN}/pricing`,
            availability: "https://schema.org/InStock",
        },
    };

    const tracks = [
        {
            icon: Server,
            title: "Clients",
            body: "Company name, industry, NIS2 scope, contacts, EIN / Tax-ID, status.",
        },
        {
            icon: Network,
            title: "Subnets",
            body: "CIDR validation, IP overlap detection, VLAN tagging, gateway, DNS servers, purpose (LAN / DMZ / Guest / IoT / Management / VoIP).",
        },
        {
            icon: Layers,
            title: "Hardware",
            body: "43 pre-cataloged models across 10 vendors with CPE auto-populated for future CVE matching.",
        },
        {
            icon: KeyRound,
            title: "Important IPs",
            body: "Domain controllers, DNS servers, gateways, NAS, app servers with criticality levels.",
        },
    ];

    return (
        <>
            <SEO
                title="Client Data Suite — Lightweight IPAM for SMB Network Engineers"
                description="Track clients, subnets, hardware, and important IPs in one place. Multi-tenant by design. Built for freelancers, in-house IT, and small MSPs."
                canonicalPath="/ipam"
                schema={productSchema}
            />
            <div
                className="pt-24 md:pt-28 pb-12 mx-auto max-w-5xl px-5 md:px-10"
                data-testid="ipam-page"
            >
                <Breadcrumb items={BREADCRUMBS.ipam} />
                <Reveal>
                    <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">
                        Client Data Suite · Pro feature
                    </p>
                    <h1
                        className="mt-4 font-display font-semibold text-4xl sm:text-5xl tracking-tight leading-[1.05] max-w-3xl"
                        data-testid="ipam-title"
                    >
                        Lightweight{" "}
                        <span className="text-accent-cyan">IPAM</span> for SMB network
                        engineers.
                    </h1>
                    <p
                        className="mt-6 text-[15px] text-text-muted leading-relaxed max-w-2xl"
                        data-testid="ipam-subtitle"
                    >
                        Track clients, subnets, hardware, and important IPs in one place.
                        Multi-tenant by design. Built for freelancers, in-house IT, and
                        small MSPs who don&apos;t need enterprise NMS complexity.
                    </p>
                </Reveal>

                {/* What you can track */}
                <section className="mt-20" data-testid="ipam-track">
                    <Reveal>
                        <div className="flex items-center gap-3 mb-5">
                            <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-canvas border border-accent-cyan/30 text-accent-cyan">
                                <Layers className="w-4 h-4" />
                            </div>
                            <h2 className="font-display font-semibold text-2xl sm:text-3xl tracking-tight">
                                What you can track
                            </h2>
                        </div>
                    </Reveal>
                    <Reveal stagger className="mt-6 grid gap-4 md:grid-cols-2">
                        {tracks.map((t) => {
                            const Icon = t.icon;
                            return (
                                <RevealItem
                                    key={t.title}
                                    className="rounded-xl border border-white/[0.06] bg-surface/40 backdrop-blur-md p-5"
                                >
                                    <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-canvas border border-white/10 text-accent-cyan">
                                        <Icon className="w-4 h-4" />
                                    </div>
                                    <h3 className="mt-4 text-[15px] font-semibold text-text-primary">
                                        {t.title}
                                    </h3>
                                    <p className="mt-2 text-[13.5px] text-text-muted leading-relaxed">
                                        {t.body}
                                    </p>
                                </RevealItem>
                            );
                        })}
                    </Reveal>
                </section>

                {/* Built for multi-client workflows */}
                <section className="mt-20" data-testid="ipam-workflows">
                    <Reveal>
                        <div className="flex items-center gap-3 mb-5">
                            <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-canvas border border-accent-cyan/30 text-accent-cyan">
                                <Search className="w-4 h-4" />
                            </div>
                            <h2 className="font-display font-semibold text-2xl sm:text-3xl tracking-tight">
                                Built for multi-client workflows
                            </h2>
                        </div>
                        <div className="text-[14.5px] text-text-muted leading-[1.85] max-w-2xl space-y-4">
                            <p>
                                Global Spotlight-style search across all your clients
                                (Cmd / Ctrl + K). CSV export per client. Wizard integration —
                                open the firewall configurator with a client selected and
                                watch the subnet, vendor, and gateway fields auto-populate
                                from your IPAM data.
                            </p>
                            <p>
                                No agents to install. No on-prem collector. Browser only.
                                Designed for the kind of team that has 5 to 50 clients, not
                                500.
                            </p>
                        </div>
                    </Reveal>
                </section>

                {/* Highlights */}
                <section className="mt-20" data-testid="ipam-highlights">
                    <Reveal stagger className="grid gap-3 sm:grid-cols-2">
                        {[
                            "43 pre-cataloged hardware models, 10 vendors",
                            "CPE auto-population for future CVE matching",
                            "IP overlap detection across all subnets",
                            "CIDR validator with IPv4 + IPv6 support",
                            "Per-client CSV export for hand-offs",
                            "Audit-friendly: archived clients preserved",
                        ].map((h) => (
                            <RevealItem
                                key={h}
                                className="flex items-start gap-3 rounded-xl border border-white/[0.06] bg-surface/40 backdrop-blur-md p-4"
                            >
                                <Check className="w-4 h-4 text-accent-mint mt-0.5 shrink-0" />
                                <p className="text-[13.5px] text-text-muted leading-relaxed">
                                    {h}
                                </p>
                            </RevealItem>
                        ))}
                    </Reveal>
                </section>

                {/* Pricing CTA */}
                <section className="mt-20" data-testid="ipam-cta">
                    <Reveal>
                        <div className="rounded-2xl border border-accent-cyan/30 bg-accent-cyan/[0.04] p-8 md:p-10">
                            <div className="flex items-center gap-3 mb-3">
                                <FileDown className="w-5 h-5 text-accent-cyan" />
                                <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">
                                    Pricing
                                </p>
                            </div>
                            <h2 className="font-display font-semibold text-2xl sm:text-3xl tracking-tight">
                                Pro plan — $19.90 / month.
                            </h2>
                            <p className="mt-3 text-[14px] text-text-muted leading-relaxed max-w-2xl">
                                Up to 10 clients (15 for Founding Members). MSP plan
                                available on request.
                            </p>
                            <div className="mt-6 flex flex-col sm:flex-row gap-3">
                                <Link
                                    to="/signup"
                                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-accent-cyan text-canvas font-medium text-[13.5px] hover:bg-accent-cyan/90 transition-colors"
                                    data-testid="ipam-cta-signup"
                                >
                                    Start free
                                    <ArrowRight className="w-3.5 h-3.5" />
                                </Link>
                                <Link
                                    to="/pricing"
                                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md border border-white/10 text-text-primary font-medium text-[13.5px] hover:border-white/20 transition-colors"
                                    data-testid="ipam-cta-pricing"
                                >
                                    Compare plans
                                </Link>
                            </div>
                        </div>
                    </Reveal>
                </section>

                {/* Related */}
                <section className="mt-16" data-testid="ipam-related">
                    <Reveal>
                        <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-text-dim">
                            Related
                        </p>
                        <div className="mt-3 flex flex-wrap gap-3 text-[13px]">
                            <Link
                                to="/zeroconf"
                                className="inline-flex items-center gap-1.5 text-accent-cyan hover:underline"
                            >
                                <Shield className="w-3.5 h-3.5" />
                                Firewall Configurator
                            </Link>
                            <Link
                                to="/tools/nis2-assessment"
                                className="inline-flex items-center gap-1.5 text-accent-cyan hover:underline"
                            >
                                NIS2 self-assessment
                            </Link>
                            <Link
                                to="/tools"
                                className="inline-flex items-center gap-1.5 text-accent-cyan hover:underline"
                            >
                                Free tools
                            </Link>
                        </div>
                    </Reveal>
                </section>
            </div>
        </>
    );
};

export default Ipam;
